import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import {
  DocumentTemplateType,
  IDocument,
  INumberedDocumentView,
  WorkflowStatusType,
} from '../../api-client/autogenerated';
import {
  documentIsOverdue,
  documentTypeToReadable,
  documentTypeToUrl,
  formatDateUpcoming,
  getDocumentStatusKey,
  getTaskStatusColors,
  getUserFacingDocumentStatus,
} from '../../scripts/utils';
import { getMyWorkNumber, getMyWorkTitleLines } from './myWorkNaming';
import CircularLoader from '../loader/CircularLoader';
import { useHistory } from 'react-router-dom';
import { ArrowForward, ExpandLess, ExpandMore, Info } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { useAppUtilsContext } from '../AppUtilsContext';
import getProjectsState from '../../features/projects/selectors';
import StatusPill from '../custom-components/StatusPill';
import TableScrollIndicator from '../custom-components/TableScrollIndicator';
import { OTHER_COLOR } from './MyTasksBreakdownChart';
import { filterDocumentsWithVisibleProject } from './myWorkVisibility';
import { getCurrentSecurityGroup, getSecurityGroupsState } from '../../features/security/selectors';
import { getUserState } from '../../features/user/selectors';
import Typography from '@material-ui/core/Typography';
import AssignedUserText from '../custom-components/AssignedUserText';
import PaginationFooter from './PaginationFooter';

// Same light blue as WorkflowStatusType.ReviewComplete in getTaskStatusColors, used for every
// "Consultant Review" label in the Type column.
const CONSULTANT_REVIEW_COLOR = '#73A3FF';

// Character budgets before each line is truncated, mirroring the description truncation in
// DocumentIndexRow. The bold title line gets more room than the plain description line.
const titleTruncateFactor = window.innerWidth / 20;
const descriptionTruncateFactor = window.innerWidth / 28;

/**
 * The full-page table's two-line title cell (title over description). Each line truncates to its
 * own character budget (title gets more room than description) and both share a single
 * expand/collapse chevron so one click reveals both lines together rather than each line toggling
 * independently.
 */
function TitleCell({ top, bottom }: { top: string; bottom: string }) {
  const [truncated, setTruncated] = useState(true);

  const renderLine = (text: string, factor: number, style?: React.CSSProperties) => {
    if (text.length <= factor) return <span style={style}>{text}</span>;
    return (
      <span
        style={{
          minWidth: 0,
          // Collapsed: single line, clipped to the column. Expanded: wrap within the column
          // and grow the row vertically rather than widening the table.
          whiteSpace: truncated ? 'nowrap' : 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          overflowWrap: 'anywhere',
          ...style,
        }}
      >
        {truncated ? `${text.substr(0, Math.round(factor)).trim()}...` : text}
      </span>
    );
  };

  const isTruncatable =
    top.length > titleTruncateFactor || bottom.length > descriptionTruncateFactor;

  return (
    <span style={{ display: 'flex', alignItems: 'flex-end', maxWidth: '100%' }}>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {renderLine(top, titleTruncateFactor, { fontWeight: 600 })}
        {bottom ? renderLine(bottom, descriptionTruncateFactor) : null}
      </span>
      {isTruncatable && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setTruncated((prev) => !prev);
          }}
          // Keep the chevron at its natural size so the text shrinks/clips beside it, never
          // overlapping it.
          style={{ padding: 0, flexShrink: 0 }}
        >
          {truncated ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      )}
    </span>
  );
}

const useStyles = makeStyles(() =>
  createStyles({
    upcomingTable: {
      // height: '100%',
      borderBottom: '1px solid #EDECEC',
    },
    tableCell: {
      whiteSpace: 'nowrap',
      border: 'none',
    },
    projectDueDate: {
      fontFamily: 'Roboto',
      fontSize: '18px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '21px',
      color: '#464546', // Gray / Gray 700
      textTransform: 'capitalize',
      whiteSpace: 'pre',
      textAlign: 'center',
    },
    projectInfo: {
      fontFamily: 'Roboto',
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '21px',
      color: '#464546', // Gray / Gray 700
      textTransform: 'capitalize',
      whiteSpace: 'nowrap',
    },
    tableContainer: {
      maxHeight: 'calc(100vh - 300px)',
      '@media print': {
        maxHeight: 'none',
        overflow: 'visible',
      },
    },
    // Matches DocumentIndexHeader's sort indicator: MUI's default fade-on-hover arrow is hidden
    // and replaced with a caret pair that's always visible, dimmed when its column isn't active.
    disableDefaultSortIcon: {
      display: 'none',
    },
    sortIcon: {
      display: 'flex',
      flexDirection: 'column',
      height: '22px',
      paddingTop: '1px',
      marginLeft: '8px',
    },
    sortIconEnabled: {
      color: '#616061',
    },
    sortIconDisabled: {
      color: '#B2B1B2',
    },
  }),
);

interface DashboardUpcomingListProps {
  soonestDueDocuments: INumberedDocumentView[];
  upcomingType: string;
  loading: boolean;
  /** Project name pill color, keyed by project id — matches the My Work page's "By Project"
   * breakdown chart slice colors exactly. Omitted by the smaller Dashboard widgets, which render
   * the project name as plain text instead. */
  projectColors?: Record<string, string>;
  /** Document type pill color, keyed by document type — matches the My Work page's "By Type"
   * breakdown chart slice colors exactly. Only rendered as a pill when viewing a single project
   * (the Type column reads as plain text otherwise, matching how the Project pill only appears
   * viewing all projects). */
  docTypeColors?: Record<string, string>;
  /** Rows-per-page selector options — the first entry is the default. Defaults to 25/50/100, so
   * this only needs to be set where a caller wants different pagination (e.g. the Dashboard's
   * embedded My Work card). */
  rowsPerPageOptions?: number[];
}

export default function DashboardUpcomingList(props: DashboardUpcomingListProps) {
  const classes = useStyles();
  const {
    soonestDueDocuments,
    upcomingType,
    loading,
    projectColors,
    docTypeColors,
    rowsPerPageOptions,
  } = props;
  const history = useHistory();

  const user = useSelector(getUserState);
  const currentSecurity = useSelector(getCurrentSecurityGroup);
  const allSecurity = useSelector(getSecurityGroupsState);
  const projects = useSelector(getProjectsState);
  const currentProject = useSelector(getProjectState);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  const {
    documentTemplates: { getTemplateId },
  } = useAppUtilsContext();

  const additionalReviewDocumentTemplateId = useMemo(
    () => getTemplateId(DocumentTemplateType.AdditionalReview),
    [getTemplateId],
  );

  const canSeeConsultantBadge = useCallback(
    (document: INumberedDocumentView) => {
      if (user.isSiteAdmin) return true;

      const project = projects.find((p) => p.id === document.projectId);
      if (project?.subscriberId === user.adminOfSubscriberId) return true;

      const relevantSecurity =
        currentSecurity || allSecurity.find((x) => x.subscriberId === project?.subscriberId);

      const additionalReviewPermission = relevantSecurity?.securityGroupDocumentTemplateList?.find(
        (x) => x.documentTemplateId === additionalReviewDocumentTemplateId,
      )?.securityPermissionLevel;
      return !!additionalReviewPermission && additionalReviewPermission >= 2;
    },
    [user, currentSecurity, projects, allSecurity, additionalReviewDocumentTemplateId],
  );

  const {
    documentTemplates: { documentTemplateIdToName },
  } = useAppUtilsContext();

  const pageSizeOptions = rowsPerPageOptions ?? [25, 50, 100];

  // The rows this table can actually render, decorated with the project each one belongs to (the
  // Project column and the project sort both need it). A document whose project isn't in the
  // portal's visible project list has no name to show and nowhere to navigate to, so it is
  // dropped — see myWorkVisibility. Resolved once here rather than inside mapDocs so the page
  // count and empty state below are derived from the same rows the body renders; deriving them
  // from the raw prop is what handed out trailing pages the body could never fill.
  const renderableDocuments = useMemo(
    () =>
      filterDocumentsWithVisibleProject(soonestDueDocuments, projects).map((doc) => ({
        ...doc,
        project: projects.find((p) => p.id === doc.projectId),
      })) as INumberedDocumentView[],
    [soonestDueDocuments, projects],
  );

  const [page, setPage] = useState(0);
  const [rowsLength, setRowsLength] = useState(pageSizeOptions[0]);
  const [orderBy, setOrderBy] = useState<'project' | 'dueDate' | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: 'project' | 'dueDate') => {
    if (orderBy !== column) {
      setOrderBy(column);
      setOrder('asc');
    } else {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    }
  };

  const renderSortableHeader = (column: 'project' | 'dueDate', label: string) => (
    <TableSortLabel
      active={orderBy === column}
      direction={orderBy === column ? order : 'asc'}
      onClick={() => handleSort(column)}
      classes={{ icon: classes.disableDefaultSortIcon }}
    >
      {label}
      <div className={classes.sortIcon}>
        <FontAwesomeIcon
          icon={faCaretUp}
          style={{ marginBottom: '-3px' }}
          className={orderBy === column ? classes.sortIconEnabled : classes.sortIconDisabled}
        />
        <FontAwesomeIcon
          icon={faCaretDown}
          style={{ marginTop: '-3px' }}
          className={orderBy === column ? classes.sortIconEnabled : classes.sortIconDisabled}
        />
      </div>
    </TableSortLabel>
  );

  // Abbreviated/short type label from the shared documentTypeToReadable list (e.g. RFI, PCO, ASI).
  const readableType = (documentType: DocumentTemplateType | undefined) =>
    documentType ? documentTypeToReadable[documentType] ?? documentType : 'Type';

  // Colored pill for a document type, matching that type's slice in the "By Type" breakdown
  // chart — only shown viewing a single project, same as the Project column's pill only showing
  // viewing all projects (docTypeColors is only meaningful in that scope).
  const renderDocType = (documentType: DocumentTemplateType | undefined, label: string) =>
    currentProject && docTypeColors ? (
      <StatusPill
        backgroundColor={(documentType && docTypeColors[documentType]) ?? OTHER_COLOR}
        fillWidth
      >
        {label}
      </StatusPill>
    ) : (
      <Typography style={{ fontSize: 18, fontWeight: 600 }}>{label}</Typography>
    );

  const getDocType = (doc: INumberedDocumentView) => {
    if (doc.additionalReviewForDocument?.documentTemplateId) {
      const reviewedType =
        documentTemplateIdToName[doc.additionalReviewForDocument.documentTemplateId];
      return (
        <div style={{ width: '100%' }}>
          <Typography align="center" style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
            {renderDocType(reviewedType, readableType(reviewedType))}
          </Typography>
          <StatusPill
            fillWidth
            backgroundColor={CONSULTANT_REVIEW_COLOR}
            style={{ marginBottom: 2 }}
          >
            Consultant Review
          </StatusPill>
          {doc.assignedToUser && doc.assignedToUserId !== user.id ? (
            <AssignedUserText
              horizontal
              name={doc.assignedToUser.name}
              company={doc.assignedToUser.company?.name}
            />
          ) : null}
        </div>
      );
    }

    if (!doc.additionalReviewRecommendedById) {
      const documentType = documentTemplateIdToName[doc.documentTemplateId];
      return renderDocType(documentType, readableType(documentType));
    }

    return <span style={{ color: CONSULTANT_REVIEW_COLOR }}>Consultant Review</span>;
  };

  const getBadge = (doc: INumberedDocumentView) => {
    if (!canSeeConsultantBadge(doc)) return null;

    if (!doc.additionalReviewDocuments?.length) return null;

    // Rows in this list are always pre-filtered to non-terminal base documents (see
    // getSoonestDueDocumentsSummary), so no "document is complete -> hide both" check is needed
    // here the way DocumentIndexRow's getConsultantBadge needs one.
    const hasOpenConsultantReview = doc.additionalReviewDocuments.some(
      (x) =>
        !x.workflowStatus ||
        ![WorkflowStatusType.ReviewComplete, WorkflowStatusType.Accepted].includes(
          x.workflowStatus,
        ),
    );
    const hasCompletedConsultantReview = doc.additionalReviewDocuments.some(
      (x) =>
        !!x.workflowStatus &&
        [WorkflowStatusType.ReviewComplete, WorkflowStatusType.Accepted].includes(x.workflowStatus),
    );
    if (!hasOpenConsultantReview && !hasCompletedConsultantReview) return null;

    return (
      <>
        {hasOpenConsultantReview && (
          <StatusPill backgroundColor="#73A3FF" fillWidth style={{ cursor: 'pointer' }}>
            Assigned to Consultant
          </StatusPill>
        )}
        {hasCompletedConsultantReview && (
          <StatusPill
            // Same colors as WorkflowStatusType.Resolved (darker blue) and .ReviewComplete (light
            // blue) in getTaskStatusColors, so this badge reads consistently with the status pill.
            backgroundColor="#F28B00"
            fillWidth
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Consultant Review Complete
            <ArrowForward fontSize="small" style={{ marginLeft: 2 }} />
          </StatusPill>
        )}
      </>
    );
  };

  // Number-only value per type (the bold identifier column in the full-page table).
  const getNumberOnly = (doc: INumberedDocumentView): string => getMyWorkNumber(doc, typeOf);

  /**
   * Building / Floor / Location names for a punch list item, stacked vertically in the Number
   * column. Empty levels are omitted.
   *
   * Read off the document's own graph (MY_WORK_GRAPH carries physicalLocation.floor.building)
   * rather than the project slice in Redux. That slice only ever holds the ONE open project's
   * buildings, so in the all-projects view every lookup missed and this column came out blank --
   * and the single-project view only worked because it happened to be looking at the project whose
   * buildings were loaded.
   */
  const getPunchListLocationLines = (doc: INumberedDocumentView): string[] => {
    const location = doc.physicalLocation;
    const floor = location?.floor;
    return [floor?.building?.name, floor?.name, location?.name].filter((v): v is string => !!v);
  };

  const typeOf = (doc: INumberedDocumentView) => documentTemplateIdToName[doc.documentTemplateId];
  const getTitleLines = (doc: INumberedDocumentView) => getMyWorkTitleLines(doc, typeOf);

  const handleClick = (doc: INumberedDocumentView) => {
    const documentType = documentTemplateIdToName[doc.documentTemplateId];

    let link = '';
    if (documentType && doc.projectId) {
      if (documentType !== DocumentTemplateType.AdditionalReview && doc.id) {
        link = `/main/projects/${doc.projectId}/documents/${documentTypeToUrl[documentType]}/${doc.id}`;
        if (documentType === DocumentTemplateType.PunchList) {
          link += '/edit';
        }
      } else if (doc.additionalReviewForDocument) {
        const parentDocumentType =
          documentTemplateIdToName[doc.additionalReviewForDocument.documentTemplateId];
        link = `/main/projects/${doc.projectId}/documents/${documentTypeToUrl[parentDocumentType]}/${doc.additionalReviewForDocument.id}`;
      }
      history.push(link);
    }
  };

  const mapDocs = (pageNum: number, rowsLength: number) => {
    if (renderableDocuments) {
      const sortedDocuments = orderBy
        ? [...renderableDocuments].sort((a, b) => {
            const cmp =
              orderBy === 'project'
                ? (a.project?.name || '').localeCompare(b.project?.name || '')
                : (a.dueDate ? new Date(a.dueDate).getTime() : 0) -
                  (b.dueDate ? new Date(b.dueDate).getTime() : 0);
            return order === 'asc' ? cmp : -cmp;
          })
        : renderableDocuments;
      if (sortedDocuments.length > pageNum * rowsLength) {
        const docsOnPage = sortedDocuments.slice(
          pageNum * rowsLength,
          Math.min(pageNum * rowsLength + rowsLength, renderableDocuments.length),
        );
        return docsOnPage.map((doc) => {
          const documentType = documentTemplateIdToName[doc.documentTemplateId];

          return (
            <TableRow key={doc.id} onClick={() => handleClick(doc)} style={{ cursor: 'pointer' }}>
              <TableCell
                className={classes.projectDueDate}
                style={{
                  paddingLeft: 12,
                  boxShadow: `inset 10px 0 0 ${
                    getTaskStatusColors(getDocumentStatusKey(doc, documentType), doc.priority).bg
                  }`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {formatDateUpcoming(doc.dueDate ? doc.dueDate : '')}
                </div>
              </TableCell>
              {!currentProject ? (
                <TableCell className={classes.projectInfo}>
                  {projectColors ? (
                    <StatusPill
                      backgroundColor={projectColors[doc.projectId] ?? OTHER_COLOR}
                      fillWidth
                    >
                      {doc.project?.name}
                    </StatusPill>
                  ) : (
                    doc.project?.name
                  )}
                </TableCell>
              ) : null}
              <TableCell className={classes.projectInfo}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {getDocType(doc)}
                  {documentType === DocumentTemplateType.PunchList && doc.customContractor && (
                    <Tooltip
                      arrow
                      placement="top"
                      title={`Assigned to Subcontractor: ${doc.customContractor}`}
                    >
                      <Info style={{ marginLeft: 4 }} />
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell
                className={classes.projectInfo}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}
              >
                {documentType === DocumentTemplateType.PunchList ? (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {getPunchListLocationLines(doc).map((line, i) => (
                      <span key={i}>{line}</span>
                    ))}
                  </div>
                ) : (
                  getNumberOnly(doc)
                )}
              </TableCell>
              <TableCell
                className={classes.projectInfo}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                <TitleCell {...getTitleLines(doc)} />
              </TableCell>
              <TableCell className={classes.projectInfo}>
                {(() => {
                  // Color the status like a document table: resolve the meaningful status
                  // (the punch list status for punch list items, an action-taken value once a
                  // submittal is Completed, otherwise the workflow status) and render it as a
                  // pill. The Status column stacks the status pill, the consultant-review
                  // badge, and an Overdue pill.
                  const { bg, fg } = getTaskStatusColors(
                    getDocumentStatusKey(doc, documentType),
                    doc.priority,
                  );
                  return (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 4,
                      }}
                    >
                      <StatusPill backgroundColor={bg} color={fg} fillWidth>
                        {getUserFacingDocumentStatus(doc, documentType)}
                      </StatusPill>
                      {getBadge(doc)}
                      {documentIsOverdue(doc) && (
                        <StatusPill backgroundColor="#E2445C" fillWidth>
                          Overdue
                        </StatusPill>
                      )}
                    </div>
                  );
                })()}
              </TableCell>
            </TableRow>
          );
        });
      }
    }
    return <div />;
  };

  const displayText = () => {
    if (renderableDocuments.length === 0 && upcomingType === 'assigned-to-me')
      return (
        <h2 style={{ fontSize: '1rem', paddingTop: '5px' }}>
          No upcoming documents assigned to you.
        </h2>
      );
    if (renderableDocuments.length === 0 && upcomingType === 'assigned-by-me')
      return (
        <h2 style={{ fontSize: '1rem', paddingTop: '5px' }}>
          No upcoming documents assigned by you to others.
        </h2>
      );
    if (renderableDocuments.length === 0 && upcomingType === 'all')
      return (
        <h2 style={{ fontSize: '1rem', paddingTop: '5px' }}>
          No upcoming documents in this project.
        </h2>
      );
  };

  const displayTable = () => {
    // Columns are weighted to their content, matching the document tables: a Number column and a
    // two-line Title rather than one combined "Item".
    // Due shows a compact stacked date (e.g. "20 / Jan."), so pin it to just enough width and
    // give the freed space to the Title column.
    const columnWidths: Record<string, string> = currentProject
      ? { Due: '72px', Type: '16%', Number: '14%', Title: '52%', Status: '12%' }
      : { Due: '72px', Type: '14%', Number: '12%', Title: '46%', Status: '10%', Project: '14%' };
    const headerStyle = (col: string): React.CSSProperties => ({ width: columnWidths[col] });

    return (
      <div style={{ position: 'relative' }}>
        <TableContainer
          ref={tableScrollRef}
          style={{
            overflowY: 'visible',
            backgroundColor: '#FFFFFF',
            borderRadius: 8,
            // Match the elevation of the DocumentIndex table card.
            boxShadow: '0px 5px 15px rgb(0, 0, 0, .15)',
            marginTop: 16,
            maxHeight: 'calc(100vh - 360px)',
          }}
        >
          <Table stickyHeader className={classes.upcomingTable}>
            <TableHead>
              <TableRow style={{ borderTop: '1px solid #EDECEC' }}>
                <TableCell
                  className={classes.tableCell}
                  style={headerStyle('Due')}
                  sortDirection={orderBy === 'dueDate' ? order : false}
                >
                  {renderSortableHeader('dueDate', 'Due')}
                </TableCell>
                {!currentProject ? (
                  <TableCell
                    className={classes.tableCell}
                    style={headerStyle('Project')}
                    sortDirection={orderBy === 'project' ? order : false}
                  >
                    {renderSortableHeader('project', 'Project')}
                  </TableCell>
                ) : null}
                <TableCell align="center" className={classes.tableCell} style={headerStyle('Type')}>
                  Type
                </TableCell>
                <TableCell className={classes.tableCell} style={headerStyle('Number')}>
                  Number
                </TableCell>
                <TableCell className={classes.tableCell} style={headerStyle('Title')}>
                  Title
                </TableCell>
                <TableCell className={classes.tableCell} style={headerStyle('Status')}>
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{mapDocs(page, rowsLength)}</TableBody>
          </Table>
        </TableContainer>
        <TableScrollIndicator scrollRef={tableScrollRef} />
        <TableScrollIndicator scrollRef={tableScrollRef} direction="horizontal" />
      </div>
    );
  };

  // Also covers the projects list arriving after the documents did: that changes which rows are
  // renderable, so the current page can no longer be assumed to exist.
  useEffect(() => {
    setPage(0);
  }, [renderableDocuments]);

  return (
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {loading && (
        <div style={{ paddingTop: '50px', paddingBottom: '50px' }}>
          <CircularLoader />
        </div>
      )}
      {!loading && (
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {renderableDocuments.length > 0 ? displayTable() : displayText()}
        </div>
      )}
      <PaginationFooter
        pageCount={Math.ceil(renderableDocuments.length / rowsLength)}
        page={page}
        onPageChange={setPage}
        rowsPerPage={rowsLength}
        rowsPerPageOptions={pageSizeOptions}
        onRowsPerPageChange={(value) => {
          setRowsLength(value);
          setPage(0);
        }}
        style={{ marginTop: 32 }}
      />
    </div>
  );
}
